<script setup lang="ts">
  import { PlanType } from '~/generated/api-clients-generated'
  import { storeToRefs } from 'pinia'
  import type { QuotedPlanVm } from '~/models/quoting/QuotedPlan'
  import type { PlanBannerConfig } from '~/composables/PlanBanners'
  import { InsuranceNeedsVisibilityGateStore } from '~/stores/insuranceNeedsVisibilityGate'

  const props = defineProps<{
    id: string
    quote: QuotedPlanVm
    banner?: Partial<PlanBannerConfig & { text: string }> | null
    stacked?: boolean
  }>()

  const { getId } = Id.use(props.id)
  const { getTooltipContent, hydratePlaceholders } = Cms.use()
  const { banner } = await PlanBanners.use(props.quote.medicareId)
  const { content: starRatingTooltip } = await getTooltipContent('MedicareStarRating')
  const { show } = DialogController.use()
  const { hasDrugsOrDocsApplied } = storeToRefs(InsuranceNeedsVisibilityGateStore.use())
  const { quotingConfig } = QuotingConfig.use()
  const session = SessionStore.use()
  const { flag } = FeatureFlags.use()
  const engine = EngineStore.use()
  const checkupStore = PlanCheckupStore.use()

  const usedBanner = computed(() => props.banner ?? banner.value)

  const isGhostCardConfig = computed(() => quotingConfig.value.ghostCard)

  const showPlanFitModal = computed(() => flag<boolean>('plan-fit-modal').value)

  const checkupMode = computed(
    () =>
      engine.id === checkupStore.currentCheckup?.engineId &&
      engine.selectedPlanType === (checkupStore.currentCheckup?.desiredPlanType ?? PlanType.MAPD)
  )

  const shouldHidePlanFitScore = computed(
    () =>
      !hasDrugsOrDocsApplied.value &&
      isGhostCardConfig.value &&
      !session.ghostCardOptOut &&
      !checkupMode.value
  )

  const canOpenPlanFitModal = computed(
    () => showPlanFitModal.value && !shouldHidePlanFitScore.value
  )

  const onShowPlanFitModal = () => {
    if (canOpenPlanFitModal.value) {
      show(getId.value('score-sidebar'))
    }
  }
</script>

<template>
  <header class="w-full">
    <aside
      v-if="!!usedBanner && hasDrugsOrDocsApplied"
      class="header-banner"
      :class="usedBanner?.color === 'green' ? 'bg-success' : 'bg-sky-800'"
    >
      <HpIcon v-if="!!usedBanner?.icon" :name="usedBanner.icon" class="size-5" />
      {{ usedBanner?.text }}
    </aside>

    <div class="px-2 pb-5 sm:pb-0">
      <figure class="flex w-full flex-col items-center sm:w-3/5 sm:items-start">
        <PxCarrierLogo
          :class="['carrier-logo', `${quote.details.carrierKey}`]"
          :logo-key="quote.details.logoKey!"
          :plan-year="quote.details.year!"
          :logo-alt="`${quote.details.logoKey} carrier logo`"
        />
        <figcaption
          class="text-balance text-center leading-tight text-gray-800 sm:text-left"
          :class="{ 'line-clamp-2 min-h-12 truncate': stacked }"
        >
          {{ quote.name }}
        </figcaption>
      </figure>

      <div
        class="mt-2 flex justify-center gap-2 sm:justify-start"
        :class="stacked ? 'flex-col' : 'flex-wrap items-center md:flex-nowrap'"
      >
        <PxStarRating
          :star-rating="quote.details.starRating"
          star-class="scale-[65%] text-[#ffc107]"
          :tooltip="{
            id: getId(`star-rating-tooltip`),
            title: starRatingTooltip.title,
            body: hydratePlaceholders(starRatingTooltip.body, { planYear: quote.details.year })
          }"
        />
        <div class="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
          <SitePartBGivebackBadge :amount="quote.recommendation.partBReduction ?? 0" />
          <div class="flex items-center gap-2">
            <QuoteCardPolicyBadges :id="getId()" :quote="quote" />
            <QuoteBadge :text="quote.details.year!.toString()" variant="white" />
          </div>
        </div>
      </div>
    </div>

    <aside
      v-if="!!quote.recommendation?.exists"
      class="plan-fit"
      :class="
        shouldHidePlanFitScore
          ? 'border-dashed border-border bg-gray-200/50'
          : 'border-success/10 bg-success-light/60'
      "
    >
      <HpAnchor
        class="flex items-center gap-1 text-[15px] text-primary-900 no-underline"
        :class="{
          'opacity-40': shouldHidePlanFitScore,
          'cursor-default': !canOpenPlanFitModal
        }"
        @click.prevent="onShowPlanFitModal"
      >
        <HpIcon name="healthpilot-icon-simple" class="size-4" />
        <span
          :class="{
            'underline decoration-gray-600 decoration-dotted underline-offset-4':
              canOpenPlanFitModal
          }"
        >
          Plan Fit Score
        </span>
      </HpAnchor>

      <HpIcon
        v-if="shouldHidePlanFitScore"
        name="help-simple"
        class="mx-auto size-[4.5rem] rounded-full border-[3px] border-dashed border-border bg-white p-2 text-gray-500"
      />

      <QuotePlanScoreV2
        v-else
        :score="quote.score"
        band-thickness="thick"
        size="large"
        :class="{ 'cursor-pointer': canOpenPlanFitModal }"
        @click="onShowPlanFitModal"
      />

      <QuoteCardScoreSidebar :id="getId('score-sidebar')" :quote="quote" />
    </aside>
  </header>
</template>

<style scoped>
  .plan-fit {
    @apply z-10 grid place-content-center gap-1.5 rounded-xl border-b border-l py-3 sm:absolute sm:right-0 sm:top-0 sm:h-28 sm:w-32 sm:rounded-none sm:rounded-bl-[2rem] sm:rounded-tr-quote sm:py-0 md:h-32 md:w-36;
  }
  :deep(.carrier-logo) {
    @apply max-h-[55px] sm:object-left;
  }

  .carrier-logo.highmark {
    @apply h-full;
  }

  .header-banner {
    @apply absolute -top-[30px] left-1/2 flex -translate-x-1/2 items-center justify-center gap-1.5 whitespace-nowrap rounded-t-[1.25rem]  px-5 py-0.5 text-base font-semibold capitalize text-white md:left-7 md:translate-x-0;
  }
</style>
